import { jsonp } from '@ifeng-fe/ui_base';
import { COMMENT_API_URL } from '@src/public/constants';

export const INTERACTION_TYPES = ['like', 'ding', 'cai', 'flower', 'candle', 'cheer'];

/**
 * 从节点解析文章 base62Id：优先 link.url（如 https://news.ifeng.com/c/xxx），否则用节点 id
 * @param {object} node
 * @returns {string}
 */
export const resolveArticleId = node => {
    const url = node?.link?.url;

    if (url) {
        const match = String(url).match(/\/c\/([A-Za-z0-9]+)/);

        if (match?.[1]) {
            return match[1];
        }

        const segments = String(url)
            .split('/')
            .filter(Boolean);
        const last = segments[segments.length - 1]?.split(/[?#]/)[0];

        if (last && /^[A-Za-z0-9]+$/.test(last)) {
            return last;
        }
    }

    return node?.id || '';
};

/**
 * 批量获取互动数（like / ding / cai / flower / candle / cheer）
 * @see https://survey.news.ifeng.com/api/getaccumulator
 * @param {string[]} keys timeline_{type}_{articleId}
 */
export const getBatchAccumulators = (keys = []) => {
    if (!keys.length) {
        return Promise.resolve({ code: 1, data: { browse: {} } });
    }

    // ui_base jsonp 会对 value 做 encodeURIComponent，数组会变成逗号串；key[] 需拼进 URL
    const keyQuery = keys.map(key => `key%5B%5D=${encodeURIComponent(key)}`).join('&');

    return jsonp(`https://survey.news.ifeng.com/api/getaccumulator?${keyQuery}`, {
        data: {
            format: 'js',
        },
        jsonpCallback: 'timelineGetAccumulatorCallback',
        cache: false,
    });
};

/**
 * 批量获取评论数
 * @see http://doc.staff.ifeng.com/apidoc/comment/#api-04_Count-getCount
 * @param {string[]} docUrls 如 ['ucms_xxx', 'ucms_yyy']
 */
export const getBatchCommentCounts = (docUrls = []) => {
    if (!docUrls.length) {
        return Promise.resolve([]);
    }

    return jsonp(`${COMMENT_API_URL}/get/count`, {
        data: {
            doc_url: docUrls.join('|'),
            format: 'js',
        },
        jsonpCallback: 'timelineGetCommentCountCallback',
        cache: false,
    });
};

/**
 * 获取单个文档评论数（取接口 count 字段）
 * @param {string} docId 文章 / 时间线 base62Id（无 ucms_ 前缀）
 * @returns {Promise<number>}
 */
export const getCommentCountByDocId = async docId => {
    if (!docId) {
        return 0;
    }

    try {
        const res = await getBatchCommentCounts([`ucms_${docId}`]);
        const list = normalizeCommentCountList(res);
        const target = `ucms_${docId}`;
        const item = list.find(i => i.doc_url === target) || list[0];

        return Number(item?.count) || 0;
    } catch (e) {
        console.error(e);

        return 0;
    }
};

const normalizeAccumulatorBrowse = res => {
    const data = res?.data?.browse ? res.data : res?.data?.data || res?.data || {};

    return data.browse || {};
};

const normalizeCommentCountList = res => {
    if (Array.isArray(res)) {
        return res;
    }

    if (Array.isArray(res?.data)) {
        return res.data;
    }

    // 单文档时接口直接返回对象，而非数组
    if (res?.doc_url) {
        return [res];
    }

    if (res?.data?.doc_url) {
        return [res.data];
    }

    return [];
};

/**
 * 为主流 nodes 批量补齐互动数与评论数（一对一写回 interaction）
 * @param {object[]} nodes
 * @returns {Promise<object[]>}
 */
export const enrichNodesWithCounts = async nodes => {
    if (!Array.isArray(nodes) || !nodes.length) {
        return nodes || [];
    }

    const articleIds = [...new Set(nodes.map(resolveArticleId).filter(Boolean))];
    const keys = [];

    articleIds.forEach(articleId => {
        INTERACTION_TYPES.forEach(type => {
            keys.push(`timeline_${type}_${articleId}`);
        });
    });

    const docUrls = articleIds.map(id => `ucms_${id}`);

    let browse = {};
    let commentList = [];

    try {
        const [accRes, countRes] = await Promise.all([
            getBatchAccumulators(keys),
            getBatchCommentCounts(docUrls),
        ]);

        browse = normalizeAccumulatorBrowse(accRes);
        commentList = normalizeCommentCountList(countRes);
    } catch (e) {
        console.error(e);

        return nodes;
    }

    const commentMap = {};

    commentList.forEach(item => {
        const docUrl = item?.doc_url || '';
        const articleId = String(docUrl).replace(/^ucms_/, '');

        if (!articleId) {
            return;
        }

        // 展示用评论数取接口 count 字段
        commentMap[articleId] = Number(item.count) || 0;
    });

    return nodes.map(node => {
        const articleId = resolveArticleId(node);
        const interaction = { ...(node.interaction || {}) };

        INTERACTION_TYPES.forEach(type => {
            const key = `timeline_${type}_${articleId}`;
            const prev = interaction[type] || {};

            interaction[type] = {
                ...prev,
                count: Number(browse[key]) || 0,
                choosen: prev.choosen,
            };
        });

        if (articleId && commentMap[articleId] != null) {
            interaction.comment = commentMap[articleId];
        }

        return {
            ...node,
            articleId,
            interaction,
        };
    });
};
